import * as React from "react";
import { useParams } from "react-router-dom";
import {
  TrendingUp,
  Wallet,
  Home,
  CreditCard,
  Plus,
  RefreshCw,
  AlertCircle,
  Pencil,
  Trash2,
  Car,
  X,
  DollarSign,
  LayoutGrid,
  LayoutList,
} from "lucide-react";

import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import { Card, CardContent } from "../../components/ui-v2/card";
import { SearchInput } from "../../components/ui-v2/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  SortableTableHead,
} from "../../components/ui-v2/table";
import { Badge } from "../../components/ui-v2/badge";
import { Button, RowIconAction } from "../../components/ui-v2/button";
import { Skeleton } from "../../components/ui-v2/skeleton";
import { FilterPill, FilterPillGroup } from "../../components/ui-v2/filter-pill";
import { StatCard } from "../../components/ui-v2/stat-card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationSummary,
} from "../../components/ui-v2/pagination";
import { CurrencyValue, PercentValue } from "../../components/ui-v2/value-display";
import { Heading, Text, MonoText } from "../../components/ui-v2/typography";
import { AddHoldingDialog } from "../../components/ui-v2/AddHoldingDialog";
import { EditHoldingDialog } from "../../components/ui-v2/EditHoldingDialog";
import { DeleteHoldingDialog } from "../../components/ui-v2/DeleteHoldingDialog";
import { SellHoldingDialog } from "../../components/ui-v2/SellHoldingDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui-v2/tooltip";
import { usePortfolio } from "../../hooks/usePortfolio";
import { useBreakpoint } from "../../hooks/use-mobile";
import { HoldingCard } from "../../components/ui-v2/HoldingCard";
import { StalenessIndicator } from "../../components/ui-v2/StalenessIndicator";

/**
 * V2 Holdings Page — Institutional Console redesign (issue #236,
 * DESIGN-SPEC.md §5 "Holdings": "KPI strip (Value / Cost basis / Unrealised
 * gain / Stock performance) -> single toolbar (search, type pills, count) ->
 * dense table (Asset, Type, Units, Avg cost, Price, Value down, Weight NEW,
 * Total gain, Platform, actions) -> pagination").
 *
 * Displays holdings by type based on URL parameter (/holdings/investments,
 * /holdings/cash, /holdings/real-estate, /holdings/vehicles,
 * /holdings/liabilities) — sub-tab routing is unchanged (navRegistry.js
 * `subTabGroups.holdings`, rendered by AppLayout/PageToolbar); this
 * component keeps its own per-type `title` (Holdings is a top-level nav
 * section, NOT part of the shared "Activity" title group used by
 * Transactions/Contributions/Events).
 *
 * New per mockup: a "Weight" column (each holding's value as a % of the
 * CURRENT CATEGORY's total value — the KPI strip's own "Total value", not
 * full-portfolio weight, since this table only ever shows one category at a
 * time) and a type FilterPillGroup (Holdings.dc.html:68-74) replacing the
 * Select-less ad-hoc "active filter chips" row with real quick-filter pills;
 * the toolbar meta text now shows "<n> holdings . prices <time>" using
 * `usePortfolio().lastUpdated` (Holdings.dc.html:75).
 *
 * All prior functionality retained: mobile/tablet responsive card grid
 * (unchanged HoldingCard, no mockup coverage for card view — declared
 * deviation), search across name/ticker/platform/type, per-column sort
 * (migrated to the shared SortableTableHead primitive — behavior-equivalent:
 * both toggle asc/desc per click and expose `aria-sort`; the local
 * component's always-visible "sortable" affordance icon on inactive columns
 * is dropped in favor of spec §4.3's "sortable columns show |up-down-arrow|
 * only on the active sort" — a declared, spec-driven visual simplification,
 * not a loss of sort capability), name and platform column click-to-filter
 * (kept, restyled to plain `text-muted-token` — no `DotBadge`/color-coding on
 * Platform, see round-2 fix note below; no mockup equivalent exists for
 * either filter path, so dropping them would remove working functionality
 * with no replacement), Stock Performance tooltip, staleness indicator,
 * sell/edit/delete row actions (now RowIconAction, always visible per
 * §4.3/§7), pagination (restyled to PaginationSummary + Prev/1/2/Next pills,
 * same page-window math), empty/loading/error states, liabilities column
 * suppression. See session file for the full feature audit.
 *
 * DECLARED: the OLD table also had a third click-to-filter cell — Type — and
 * a dedicated per-row "Stock Perf." (Method B) column with its own sort key.
 * Both are intentionally NOT reproduced 1:1:
 * - Type click-to-filter now exists only via the Investments-only type
 *   FilterPillGroup (`TYPE_FILTER_PILLS`); other categories (cash/
 *   real-estate/vehicles/liabilities) have no type quick-filter UI. Net
 *   capability loss is immaterial — within a single non-Investment category
 *   `assetType` is effectively homogeneous, so a working the `columnFilters.
 *   type` state/logic remain intact and could still be driven
 *   programmatically by a future entry point.
 * - The per-row "Stock Perf." column + `gainMethodB` sort key were removed
 *   because DESIGN-SPEC.md §5 line 174 and Holdings.dc.html's header row
 *   define the table's exact column set (Asset, Type, Units, Avg cost,
 *   Price, Value down, Weight NEW, Total gain, Platform, actions) with NO
 *   Stock Perf. column — the metric is spec'd as a KPI-strip-only figure
 *   (Holdings.dc.html:54), which this file retains (`gainMethodB` still
 *   computed and feeds the Stock-performance StatCard). The column drop
 *   matches the mockup exactly; only the per-row presentation is gone, not
 *   the metric.
 *
 * ROUND-2 FIX (ui-design-expert review findings B1/B2, both undeclared in
 * round 1):
 * - B1: Platform cell was a color-coded `DotBadge` pill whose dot color was
 *   driven by the row's gain sign — an invented, color-only semantic signal
 *   on a non-P&L column, and a pill radius §2.4 reserves for status
 *   dots/avatar only. Fixed to plain `text-[11.5px] text-muted-token`
 *   (Holdings.dc.html:103), keeping the click-to-filter `<button>` wrapper.
 * - B2: "Refresh prices" had been relocated into the `HoldingsToolbar` filter
 *   card instead of the 46px page toolbar. Fixed: Refresh now passes into
 *   `AppLayout`'s `secondaryActions` slot alongside the "+ Add holding"
 *   `primaryAction`, matching Holdings.dc.html:30-33 and the same
 *   Transactions.jsx/Events.jsx toolbar-bridging pattern.
 */

const holdingTypes = {
  investments: {
    title: "Investments",
    description: "Stocks, ETFs, Crypto, and other investment holdings",
    icon: TrendingUp,
  },
  cash: {
    title: "Cash & Bank",
    description: "Cash accounts, savings, and bank deposits",
    icon: Wallet,
  },
  "real-estate": {
    title: "Real Estate",
    description: "Property and real estate holdings",
    icon: Home,
  },
  vehicles: {
    title: "Vehicles & Equipment",
    description: "Cars, boats, equipment, and other physical assets",
    icon: Car,
  },
  liabilities: {
    title: "Liabilities",
    description: "Loans, credit cards, and other debts",
    icon: CreditCard,
  },
};

// Map asset types to badge variants — Stock/ETF -> accent(info), Crypto ->
// warning, Metal -> positive(success), neutral -> secondary (§4.3).
const getAssetTypeBadgeVariant = (assetType) => {
  const variants = {
    'Stock': 'info',
    'ETF': 'info',
    'Cryptocurrency': 'warning',
    'Precious Metal': 'success',
    'Cash/Bank Account': 'secondary',
    'Real Estate': 'success',
    'Real Estate & Property': 'success',
    'Vehicle': 'secondary',
    'Equipment': 'secondary',
    'Other High-Value Asset': 'secondary',
    'Mortgage': 'destructive',
    'Loan': 'destructive',
    'Credit Card Debt': 'destructive',
    'Other Liability': 'destructive',
  };
  return variants[assetType] || 'secondary';
};

// Quick-filter pills per category (Holdings.dc.html:69-73 "All types / Stocks
// / ETFs / Crypto / Metals" for Investments; other categories fall back to
// "All types" only since their asset-type spread doesn't map to the mockup's
// investment-specific pill set).
const TYPE_FILTER_PILLS = {
  investments: [
    { value: null, label: "All types" },
    { value: "Stock", label: "Stocks" },
    { value: "ETF", label: "ETFs" },
    { value: "Cryptocurrency", label: "Crypto" },
    { value: "Precious Metal", label: "Metals" },
  ],
};

/**
 * Whether a holding can be sold/liquidated via the Sell dialog.
 *
 * `holding._raw.useManualValue` is NOT a reliable "is this sellable" signal
 * on its own: `portfolioCalculator.js` force-sets `useManualValue: true` for
 * EVERY `Cash/Bank Account` holding purely for pricing purposes (cash has no
 * market ticker to price against), regardless of what was actually stored
 * for the holding — see `portfolioCalculator.js:101-102`/`:140-141`
 * (`useManualValue = holding.useManualValue || isCashAccount`). Cash
 * holdings ARE conceptually sellable/liquidatable (bug found via #238 E2E
 * audit — SELL-02 asserts exactly this), unlike genuinely manual-value
 * illiquid assets (Real Estate, Vehicle, Equipment) where `useManualValue`
 * reflects a real user choice and correctly excludes them from "Sell".
 */
const isHoldingSellable = (holding) =>
  holding?.assetType === "Cash/Bank Account" || !holding?.useManualValue;

// Normalize holding data for display
const normalizeHolding = (holding) => {
  const units = parseFloat(holding.units) || 0;
  const purchasePrice = parseFloat(holding.purchasePrice) || 0;
  // Use native currentPrice (in trading currency) for display, not EUR-converted price
  // This matches what the old UI shows (e.g., $23.88 USD for WBD, not €20.59)
  const currentPrice = holding.currentPrice || holding.currentPriceEUR || holding.totalValueEUR / units || 0;
  const totalValue = holding.totalValueEUR || 0;
  const gain = holding.gainEUR ?? 0;
  // Calculate total cost in EUR: either from purchasePriceEUR * units, or derive from value - gain
  const totalCost = holding.purchasePriceEUR
    ? (holding.purchasePriceEUR * units)
    : (totalValue - gain);
  const gainPercent = holding.gainPercent ?? (totalCost > 0 ? (gain / totalCost) * 100 : 0);
  // Trading currency is what the stock actually trades in (e.g., USD for US stocks)
  // Falls back to purchase currency if not available
  const tradingCurrency = holding.tradingCurrency || holding.currency || 'EUR';

  // Method B: Stock performance isolated from currency movements
  // Calculates gain in native currency, then converts to EUR
  const gainMethodB = holding.gainEUR_MethodB ?? null;
  const gainPercentMethodB = holding.gainPercent_MethodB ?? null;

  return {
    // Use holdingId from API, fallback to id for compatibility
    id: holding.holdingId || holding.id,
    name: holding.name || holding.assetName || 'Unknown',
    ticker: holding.ticker || '-',
    assetType: holding.assetType || 'Other',
    units,
    purchasePrice,
    currentPrice,
    totalValue,
    totalCost,
    gain,
    gainPercent,
    gainMethodB,        // Stock performance (excludes currency impact)
    gainPercentMethodB, // Stock performance % (excludes currency impact)
    currency: holding.currency || 'EUR',
    tradingCurrency, // The currency the stock trades in (for current price display)
    platform: holding.platform || '-',
    isLiability: holding.isLiability || false,
    // Preserve raw holding data for edit/delete operations
    _raw: holding,
  };
};

function formatPriceTime(timestamp) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function LoadingState({ isLiabilities = false }) {
  const cardCount = isLiabilities ? 3 : 4;
  return (
    <div className="flex flex-col gap-3.5">
      <div className={`grid gap-3 grid-cols-2 ${isLiabilities ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
        {Array.from({ length: cardCount }, (_, i) => i + 1).map((i) => (
          <Card key={`summary-skeleton-${i}`} className="py-3.5 px-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-2 h-6 w-28" />
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-3 border-b border-hairline px-[18px] py-2.5">
        <Skeleton className="h-[30px] w-64" />
        <Skeleton className="h-[26px] w-64" />
      </div>

      <Card>
        <CardContent className="p-0">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={`row-skeleton-${i}`} className="flex items-center gap-4 border-b border-hairline px-[18px] py-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-16" />
              <Skeleton className="ml-auto h-4 w-20" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ErrorState({ error, onRetry }) {
  return (
    <Card>
      <CardContent className="py-16">
        <div className="flex flex-col items-center justify-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
          </div>
          <Heading level={4} className="mt-4">
            Failed to load holdings
          </Heading>
          <Text variant="secondary" className="mt-2 text-center max-w-md">
            {error}
          </Text>
          <Button variant="outline" className="mt-6" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ type, config, onAddHolding }) {
  const Icon = config.icon;

  return (
    <Card>
      <CardContent className="py-16">
        <div className="flex flex-col items-center justify-center">
          <div className="rounded-full bg-primary/10 p-4">
            <Icon className="h-12 w-12 text-primary" />
          </div>
          <Heading level={4} className="mt-4">
            No {config.title.toLowerCase()} yet
          </Heading>
          <Text variant="secondary" className="mt-2 text-center max-w-md">
            {config.description}
          </Text>
          <Button className="mt-6" onClick={onAddHolding}>
            <Plus className="h-4 w-4 mr-2" />
            Add your first {type === 'liabilities' ? 'liability' : 'holding'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * KPI strip — Total value / Cost basis / Unrealised gain / Stock performance
 * (Holdings.dc.html:37-60). Liabilities category drops the Stock performance
 * card (no equivalent metric) and relabels the first two per the old
 * component's isLiabilities branch (Total Debt / Original Amount / Amount
 * Paid) — same values as before, restyled as StatCards.
 */
function SummaryCards({ holdings, isLiabilities = false }) {
  const totalValue = holdings.reduce((sum, h) => sum + Math.abs(h.totalValue), 0);
  const totalCost = holdings.reduce((sum, h) => sum + h.totalCost, 0);
  const totalGain = holdings.reduce((sum, h) => sum + h.gain, 0);
  const totalGainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

  const totalStockPerf = holdings.reduce((sum, h) => sum + (h.gainMethodB || 0), 0);
  const totalStockPerfPercent = totalCost > 0 ? (totalStockPerf / totalCost) * 100 : 0;

  const eur = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(value || 0);
  const signedEur = (value) => `${value >= 0 ? "+" : "−"}${eur(Math.abs(value))}`;
  const signedPct = (value) => `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(2)}%`;

  return (
    <div className={`grid gap-3 grid-cols-2 ${isLiabilities ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
      <StatCard
        label={isLiabilities ? "Total debt" : "Total value"}
        value={eur(totalValue)}
        valueTone={isLiabilities ? "negative" : "default"}
      />
      <StatCard
        label={isLiabilities ? "Original amount" : "Cost basis"}
        value={eur(totalCost)}
      />
      <StatCard
        label={isLiabilities ? "Amount paid" : "Unrealised gain"}
        value={isLiabilities ? eur(Math.abs(totalGain)) : signedEur(totalGain)}
        valueTone={isLiabilities ? "default" : totalGain >= 0 ? "positive" : "negative"}
        delta={isLiabilities ? undefined : signedPct(totalGainPercent)}
        deltaTone={totalGainPercent >= 0 ? "positive" : "negative"}
      />
      {!isLiabilities && (
        <StatCard
          label={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    Stock performance
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>Pure stock performance without currency impact. Calculates gain in native currency, then converts to EUR.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
          value={signedEur(totalStockPerf)}
          valueTone={totalStockPerf >= 0 ? "positive" : "negative"}
          delta={signedPct(totalStockPerfPercent)}
          deltaTone={totalStockPerfPercent >= 0 ? "positive" : "negative"}
        />
      )}
    </div>
  );
}

/**
 * Single toolbar — search + type filter pills + count/prices-time meta
 * (Holdings.dc.html:63-76), plus the view-mode toggle (tablet only) and
 * active platform/name filter chips (kept — no mockup equivalent exists for
 * these, dropping them would remove a working filter path with no
 * replacement). Refresh/Add live in the AppLayout page toolbar (§3,
 * Holdings.dc.html:30-33 — secondary "Refresh prices" + primary "+ Add
 * holding" both sit in the 46px toolbar, not this filter card; round-2 fix,
 * ui-design-expert review finding B2).
 */
function HoldingsToolbar({
  searchTerm,
  onSearchChange,
  holdingCount,
  priceTime,
  typeFilterPills,
  activeTypeFilter,
  onTypeFilterChange,
  activeFilters,
  onClearFilter,
  onClearAllFilters,
  viewMode,
  onViewModeChange,
  showViewToggle = false,
}) {
  const hasCellFilters = Boolean(activeFilters.platform || activeFilters.name);

  return (
    <Card className="p-0">
      <CardContent className="flex flex-col gap-2.5 px-[18px] py-2.5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:max-w-[280px]">
            <SearchInput
              placeholder="Filter by name, ticker, platform…"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full"
            />
          </div>

          {typeFilterPills && (
            <FilterPillGroup>
              {typeFilterPills.map((pill) => (
                <FilterPill
                  key={pill.label}
                  active={activeTypeFilter === pill.value}
                  onClick={() => onTypeFilterChange(pill.value)}
                >
                  {pill.label}
                </FilterPill>
              ))}
            </FilterPillGroup>
          )}

          <div className="flex items-center justify-between gap-2 sm:ml-auto">
            {showViewToggle && (
              <div className="hidden sm:flex items-center rounded-md border border-border">
                <Button
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-8 rounded-r-none px-2"
                  onClick={() => onViewModeChange('table')}
                  aria-label="Table view"
                >
                  <LayoutList className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-8 rounded-l-none border-l px-2"
                  onClick={() => onViewModeChange('cards')}
                  aria-label="Card view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
              </div>
            )}
            <MonoText size="xs" className="whitespace-nowrap text-text-faint">
              {holdingCount} {holdingCount === 1 ? "holding" : "holdings"}
              {priceTime && ` · prices ${priceTime}`}
            </MonoText>
          </div>
        </div>

        {hasCellFilters && (
          <div className="flex items-center gap-2 flex-wrap">
            {activeFilters.platform && (
              <Badge variant="info" size="sm" className="gap-1 pr-1">
                Platform: {activeFilters.platform}
                <button
                  onClick={() => onClearFilter('platform')}
                  aria-label="Clear platform filter"
                  className="ml-1 rounded-full hover:bg-white/20 p-0.5 min-w-[20px] min-h-[20px] flex items-center justify-center"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {activeFilters.name && (
              <Badge variant="info" size="sm" className="gap-1 pr-1">
                Name: {activeFilters.name}
                <button
                  onClick={() => onClearFilter('name')}
                  aria-label="Clear name filter"
                  className="ml-1 rounded-full hover:bg-white/20 p-0.5 min-w-[20px] min-h-[20px] flex items-center justify-center"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={onClearAllFilters} className="h-6 px-2 text-xs">
              Clear all
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Clickable cell for filtering (name/platform — kept, no mockup equivalent
// to replace this working filter path; see module header deviation note).
function FilterableCell({ children, value, filterKey, onFilter, activeFilter, className = "" }) {
  const isFiltered = activeFilter === value;

  return (
    <TableCell className={className}>
      <button
        onClick={() => onFilter(filterKey, isFiltered ? null : value)}
        className={`text-left hover:underline cursor-pointer transition-colors duration-[120ms] ease-out ${
          isFiltered ? 'text-accent-text font-semibold' : ''
        }`}
        title={isFiltered ? `Click to clear filter` : `Click to filter by "${value}"`}
      >
        {children}
      </button>
    </TableCell>
  );
}

function HoldingsTable({
  holdings,
  categoryTotalValue,
  isLiabilities = false,
  onEdit,
  onDelete,
  onSell,
  sortBy,
  sortDirection,
  onSort,
  onFilter,
  activeFilters,
}) {
  if (holdings.length === 0) {
    return null;
  }

  const sortDir = (key) => (sortBy === key ? sortDirection : undefined);

  return (
    <Card className="overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead sortDirection={sortDir("name")} onSort={() => onSort("name")}>
              Asset
            </SortableTableHead>
            <SortableTableHead sortDirection={sortDir("type")} onSort={() => onSort("type")}>
              Type
            </SortableTableHead>
            <SortableTableHead className="text-right" sortDirection={sortDir("units")} onSort={() => onSort("units")}>
              {isLiabilities ? "Amount" : "Units"}
            </SortableTableHead>
            {!isLiabilities && (
              <>
                <SortableTableHead className="text-right" sortDirection={sortDir("cost")} onSort={() => onSort("cost")}>
                  Avg cost
                </SortableTableHead>
                <SortableTableHead className="text-right" sortDirection={sortDir("price")} onSort={() => onSort("price")}>
                  Price
                </SortableTableHead>
              </>
            )}
            <SortableTableHead className="text-right" sortDirection={sortDir("value")} onSort={() => onSort("value")}>
              Value
            </SortableTableHead>
            {!isLiabilities && <TableHead className="text-right">Weight</TableHead>}
            {!isLiabilities && (
              <SortableTableHead className="text-right" sortDirection={sortDir("gain")} onSort={() => onSort("gain")}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help underline decoration-dotted underline-offset-2">
                        Total gain
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p>Converts prices to EUR at historical rates, then calculates difference. Includes currency fluctuation impact.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </SortableTableHead>
            )}
            <SortableTableHead onSort={() => onSort("platform")} sortDirection={sortDir("platform")}>
              Platform
            </SortableTableHead>
            <TableHead className="text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {holdings.map((holding) => {
            const weight = categoryTotalValue > 0 ? (Math.abs(holding.totalValue) / categoryTotalValue) * 100 : 0;

            return (
              <TableRow key={holding.id}>
                <FilterableCell
                  value={holding.name}
                  filterKey="name"
                  onFilter={onFilter}
                  activeFilter={activeFilters?.name}
                >
                  <div className="flex max-w-[220px] items-center gap-1.5">
                    <span className="truncate font-medium" title={holding.name}>
                      {holding.name}
                    </span>
                    <StalenessIndicator holding={holding._raw} size="sm" />
                    {holding.ticker !== '-' && (
                      <MonoText size="xs" className="shrink-0 text-text-faint" title={holding.ticker}>
                        {holding.ticker}
                      </MonoText>
                    )}
                  </div>
                </FilterableCell>
                <TableCell>
                  <Badge variant={getAssetTypeBadgeVariant(holding.assetType)} size="sm">
                    {holding.assetType}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <MonoText size="sm">
                    {isLiabilities ? (
                      <CurrencyValue
                        value={Math.abs(holding.totalValue || holding.manualCurrentValue || 0)}
                        currency="EUR"
                        size="sm"
                      />
                    ) : holding.units?.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </MonoText>
                </TableCell>
                {!isLiabilities && (
                  <>
                    <TableCell className="text-right">
                      <CurrencyValue value={holding.purchasePrice} currency={holding.currency} size="sm" className="font-mono tabular-nums" />
                    </TableCell>
                    <TableCell className="text-right">
                      <CurrencyValue value={holding.currentPrice} currency={holding.tradingCurrency} size="sm" className="font-mono tabular-nums" />
                    </TableCell>
                  </>
                )}
                <TableCell className="text-right">
                  <CurrencyValue
                    value={Math.abs(holding.totalValue)}
                    currency="EUR"
                    size="sm"
                    colorize={isLiabilities}
                    className="font-mono font-semibold tabular-nums"
                  />
                </TableCell>
                {!isLiabilities && (
                  <TableCell className="text-right">
                    <MonoText size="xs" className="text-muted-token">
                      {weight.toFixed(1)}%
                    </MonoText>
                  </TableCell>
                )}
                {!isLiabilities && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <CurrencyValue value={holding.gain} currency="EUR" size="sm" colorize showSign className="font-mono tabular-nums" />
                      <PercentValue value={holding.gainPercent} size="xs" showTrend className="opacity-85" />
                    </div>
                  </TableCell>
                )}
                <FilterableCell
                  value={holding.platform || 'Not Set'}
                  filterKey="platform"
                  onFilter={onFilter}
                  activeFilter={activeFilters?.platform}
                >
                  {holding.platform && holding.platform !== '-' ? (
                    <span className="text-[11.5px] text-muted-token">{holding.platform}</span>
                  ) : (
                    <span className="text-[11.5px] text-text-faint">Not Set</span>
                  )}
                </FilterableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <RowIconAction intent="neutral" label={`Edit ${holding.name}`} onClick={() => onEdit(holding._raw)}>
                      <Pencil />
                    </RowIconAction>
                    {!isLiabilities && isHoldingSellable(holding._raw) && onSell && (
                      <RowIconAction intent="positive" label={`Sell ${holding.name}`} onClick={() => onSell(holding._raw)}>
                        <DollarSign />
                      </RowIconAction>
                    )}
                    <RowIconAction intent="negative" label={`Delete ${holding.name}`} onClick={() => onDelete(holding._raw)}>
                      <Trash2 />
                    </RowIconAction>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

/**
 * HoldingsCardGrid - Mobile card layout for holdings (unchanged HoldingCard
 * primitive — no mockup coverage for a card view; declared deviation, see
 * module header).
 */
function HoldingsCardGrid({ holdings, isLiabilities = false, onEdit, onDelete, onSell }) {
  if (holdings.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
      {holdings.map((holding) => (
        <HoldingCard
          key={holding.id}
          holding={holding}
          isLiability={isLiabilities}
          onEdit={onEdit}
          onDelete={onDelete}
          onSell={!isLiabilities && isHoldingSellable(holding._raw) ? onSell : undefined}
        />
      ))}
    </div>
  );
}

export function Holdings() {
  const { type = 'investments' } = useParams();
  const config = holdingTypes[type] || holdingTypes.investments;
  const { isMobile, isTablet } = useBreakpoint();

  const {
    loading,
    error,
    refresh,
    lastUpdated,
    getHoldingsByCategory,
  } = usePortfolio();

  const [searchTerm, setSearchTerm] = React.useState("");
  const [sortBy, setSortBy] = React.useState("value");
  const [sortDirection, setSortDirection] = React.useState("desc");
  const [columnFilters, setColumnFilters] = React.useState({ name: null, type: null, platform: null });
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isSellDialogOpen, setIsSellDialogOpen] = React.useState(false);
  const [selectedHolding, setSelectedHolding] = React.useState(null);
  const [viewMode, setViewMode] = React.useState('table'); // 'table' or 'cards'

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = isMobile ? 10 : 15; // Fewer items per page on mobile

  // Determine if we should show cards (mobile always shows cards)
  const showCards = isMobile || (isTablet && viewMode === 'cards');
  const showViewToggle = isTablet; // Only show toggle on tablet where both views are available

  // Determine default asset type based on current category
  const getDefaultAssetType = () => {
    switch (type) {
      case 'investments':
        return 'Stock';
      case 'cash':
        return 'Cash/Bank Account';
      case 'real-estate':
        return 'Real Estate & Property';
      case 'liabilities':
        return 'Mortgage';
      default:
        return 'Stock';
    }
  };

  // Get holdings for current category
  const categoryHoldings = React.useMemo(() => {
    return getHoldingsByCategory(type).map(normalizeHolding);
  }, [type, getHoldingsByCategory]);

  const categoryTotalValue = React.useMemo(
    () => categoryHoldings.reduce((sum, h) => sum + Math.abs(h.totalValue), 0),
    [categoryHoldings]
  );

  // Filter and sort holdings
  const filteredHoldings = React.useMemo(() => {
    let result = [...categoryHoldings];

    // Apply column filters
    if (columnFilters.name) {
      result = result.filter(h => h.name === columnFilters.name);
    }
    if (columnFilters.type) {
      result = result.filter(h => h.assetType === columnFilters.type);
    }
    if (columnFilters.platform) {
      result = result.filter(h => (h.platform || 'Not Set') === columnFilters.platform);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (h) =>
          h.name.toLowerCase().includes(term) ||
          h.ticker.toLowerCase().includes(term) ||
          h.platform.toLowerCase().includes(term) ||
          h.assetType.toLowerCase().includes(term)
      );
    }

    // Sort
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "value":
          comparison = Math.abs(a.totalValue) - Math.abs(b.totalValue);
          break;
        case "gain":
          comparison = a.gain - b.gain;
          break;
        case "type":
          comparison = a.assetType.localeCompare(b.assetType);
          break;
        case "units":
          comparison = a.units - b.units;
          break;
        case "cost":
          comparison = a.purchasePrice - b.purchasePrice;
          break;
        case "price":
          comparison = a.currentPrice - b.currentPrice;
          break;
        case "platform":
          comparison = (a.platform || '').localeCompare(b.platform || '');
          break;
        case "name":
        default:
          comparison = a.name.localeCompare(b.name);
          break;
      }
      return comparison * multiplier;
    });

    return result;
  }, [categoryHoldings, searchTerm, sortBy, sortDirection, columnFilters]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredHoldings.length / itemsPerPage);
  const paginatedHoldings = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredHoldings.slice(startIndex, endIndex);
  }, [filteredHoldings, currentPage, itemsPerPage]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, columnFilters, type]);

  // Handle column sort click
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - default to descending for numeric columns, ascending for text
      setSortBy(column);
      const numericColumns = ['value', 'gain', 'units', 'cost', 'price'];
      setSortDirection(numericColumns.includes(column) ? 'desc' : 'asc');
    }
  };

  // Handle column filter click
  const handleFilter = (filterKey, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  // Clear a single filter
  const handleClearFilter = (filterKey) => {
    setColumnFilters(prev => ({
      ...prev,
      [filterKey]: null
    }));
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setColumnFilters({ name: null, type: null, platform: null });
  };

  // Type filter pills (Holdings.dc.html:69-73) drive the same columnFilters.type
  // state as the (kept) programmatic type filter — a second entry point into
  // the same filter, not a competing one.
  const handleTypeFilterPillChange = (value) => {
    setColumnFilters(prev => ({ ...prev, type: value }));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAddHoldingSuccess = async () => {
    await handleRefresh();
  };

  const handleEditClick = (holding) => {
    setSelectedHolding(holding);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (holding) => {
    setSelectedHolding(holding);
    setIsDeleteDialogOpen(true);
  };

  const handleSellClick = (holding) => {
    setSelectedHolding(holding);
    setIsSellDialogOpen(true);
  };

  const handleEditSuccess = async () => {
    await handleRefresh();
  };

  const handleDeleteSuccess = async () => {
    await handleRefresh();
  };

  const handleSellSuccess = async () => {
    await handleRefresh();
  };

  const isLiabilities = type === 'liabilities';
  const typeFilterPills = TYPE_FILTER_PILLS[type] || null;
  const priceTime = formatPriceTime(lastUpdated);

  const primaryAction = (
    <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
      <Plus className="h-3.5 w-3.5 mr-1.5" />
      Add holding
    </Button>
  );

  // Secondary "Refresh prices" toolbar action (Holdings.dc.html:30-33, §3:
  // primary + secondary actions both live in the 46px page toolbar — round-2
  // fix, ui-design-expert review finding B2. Mirrors the Transactions.jsx/
  // Events.jsx secondaryActions pattern.)
  const secondaryActions = (
    <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
      <RefreshCw className={`h-3.5 w-3.5 sm:mr-1.5 ${isRefreshing ? "animate-spin motion-reduce:animate-none" : ""}`} />
      <span className="hidden sm:inline">Refresh prices</span>
    </Button>
  );

  // Render states
  if (loading) {
    return (
      <AppLayout title={config.title} primaryAction={primaryAction} secondaryActions={secondaryActions}>
        <LoadingState isLiabilities={isLiabilities} />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title={config.title} primaryAction={primaryAction} secondaryActions={secondaryActions}>
        <ErrorState error={error} onRetry={handleRefresh} />
      </AppLayout>
    );
  }

  if (categoryHoldings.length === 0) {
    return (
      <AppLayout title={config.title} primaryAction={primaryAction} secondaryActions={secondaryActions}>
        <EmptyState
          type={type}
          config={config}
          onAddHolding={() => setIsAddDialogOpen(true)}
        />
        <AddHoldingDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          onSuccess={handleAddHoldingSuccess}
          defaultAssetType={getDefaultAssetType()}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout title={config.title} primaryAction={primaryAction} secondaryActions={secondaryActions}>
      <div className="flex flex-col gap-3.5">
        <SummaryCards holdings={filteredHoldings} isLiabilities={isLiabilities} />
        <HoldingsToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          holdingCount={filteredHoldings.length}
          priceTime={priceTime}
          typeFilterPills={typeFilterPills}
          activeTypeFilter={columnFilters.type}
          onTypeFilterChange={handleTypeFilterPillChange}
          activeFilters={columnFilters}
          onClearFilter={handleClearFilter}
          onClearAllFilters={handleClearAllFilters}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showViewToggle={showViewToggle}
        />

        {/* Card view for mobile, Table view for desktop */}
        {showCards ? (
          <HoldingsCardGrid
            holdings={paginatedHoldings}
            isLiabilities={isLiabilities}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onSell={handleSellClick}
          />
        ) : (
          <HoldingsTable
            holdings={paginatedHoldings}
            categoryTotalValue={categoryTotalValue}
            isLiabilities={isLiabilities}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onSell={handleSellClick}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            onFilter={handleFilter}
            activeFilters={columnFilters}
          />
        )}

        {/* Pagination (§4.3: "1-15 of 25" mono left; Prev/1/2/Next pills right) */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center justify-between gap-3 px-1 sm:flex-row">
            <PaginationSummary>
              {((currentPage - 1) * itemsPerPage) + 1}&ndash;{Math.min(currentPage * itemsPerPage, filteredHoldings.length)} of {filteredHoldings.length}
            </PaginationSummary>
            <Pagination className="mx-0 w-auto">
              <PaginationContent className="flex-wrap justify-center gap-1">
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>

                <PaginationItem>
                  <PaginationLink isActive={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    1
                  </PaginationLink>
                </PaginationItem>

                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages) return null;
                  const showOnMobile = page === currentPage;
                  const showOnDesktop = page >= currentPage - 1 && page <= currentPage + 1;
                  if (!showOnDesktop) return null;
                  return (
                    <PaginationItem key={`page-${page}`} className={showOnMobile ? '' : 'hidden sm:block'}>
                      <PaginationLink isActive={currentPage === page} onClick={() => setCurrentPage(page)}>
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                {totalPages > 1 && (
                  <PaginationItem>
                    <PaginationLink isActive={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      {/* Add Holding Dialog */}
      <AddHoldingDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSuccess={handleAddHoldingSuccess}
        defaultAssetType={getDefaultAssetType()}
      />

      {/* Edit Holding Dialog */}
      <EditHoldingDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={handleEditSuccess}
        holding={selectedHolding}
      />

      {/* Delete Holding Dialog */}
      <DeleteHoldingDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
        holding={selectedHolding}
      />

      {/* Sell Holding Dialog */}
      <SellHoldingDialog
        open={isSellDialogOpen}
        onOpenChange={setIsSellDialogOpen}
        onSuccess={handleSellSuccess}
        holding={selectedHolding}
      />
    </AppLayout>
  );
}

export default Holdings;
